import { IonIcon, IonChip, IonLabel } from "@ionic/react";
import { filterOutline, closeCircle } from "ionicons/icons";
import { useState, useEffect, useContext, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import DetailedBlockFeedNew from "./DetailedBlockFeedNew";
import { FilterModalBlockTrades } from "./FilterBlockTrades";
import { send } from "../../utils/sockets/asyncSocket";
import config from "../helpers/socket/config";
import {
  GET_BLOCK_FEED_FILTERS_ACTION,
  UPDATE_BLOCK_FEED_FILTER_ACTION,
  DELETE_BLOCK_FEED_FILTER_ACTION,
} from "./constants";
import { BlockFeedFilter } from "./type";
import AppPrefContext from "../contexts/AppPrefContext";
import { pubsubEvents } from "../helpers/socket/pubsubEvent";

const BlockFeedContentNew = ({
  currentSelectedProfile,
}: {
  currentSelectedProfile: number | undefined;
}) => {
  const [assetClassActiveFilters, setAssetClassActiveFilters] = useState<
    Record<string, Set<string>>
  >({});
  const [blockContentActiveFilter, SetBlockContentActiveFilter] = useState<
    Set<string>
  >(new Set());
  const [exchangesFilter, setExchangesFilter] = useState<Set<string>>(
    new Set(),
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("topday");

  // Saved Filters State
  const [savedFilters, setSavedFilters] = useState<BlockFeedFilter[]>([]);
  const [activeSavedFilterId, setActiveSavedFilterId] = useState<number | null>(
    null,
  );
  const filterChipsWrapperRef = useRef<HTMLDivElement>(null);

  const appPref = useContext(AppPrefContext);

  const applySavedFilterToState = (filter: BlockFeedFilter) => {
    setActiveSavedFilterId(filter.id!);

    // Transform backend structure to frontend state structure
    const newAssetFilters: Record<string, Set<string>> = {};
    if (filter.assetClassFilters) {
      Object.entries(filter.assetClassFilters).forEach(([key, values]) => {
        newAssetFilters[key] = new Set(values);
      });
    }
    setAssetClassActiveFilters(newAssetFilters);

    SetBlockContentActiveFilter(new Set(filter.blockTypes || []));
    setExchangesFilter(new Set(filter.exchanges || []));
  };

  // parsing filters from the BE
  const processFetchedFilters = (backendPayload: any[]) => {
    return backendPayload.map((filter) => {
      let parsedAssetClassFilters = {};
      if (typeof filter.assetClassFilters === "string") {
        try {
          parsedAssetClassFilters = JSON.parse(filter.assetClassFilters);
        } catch (e) {
          console.error("Failed to parse assetClassFilters", e);
        }
      } else if (filter.assetClassFilters != null) {
        parsedAssetClassFilters = filter.assetClassFilters;
      }

      let parsedBlockTypes = [];
      if (typeof filter.blockTypes === "string") {
        try {
          parsedBlockTypes = JSON.parse(filter.blockTypes);
        } catch (e) {
          console.error("Failed to parse blockTypes", e);
        }
      } else if (filter.blockTypes != null) {
        parsedBlockTypes = filter.blockTypes;
      }

      let parsedExchanges = [];
      if (typeof filter.exchanges === "string") {
        try {
          parsedExchanges = JSON.parse(filter.exchanges);
        } catch (e) {
          console.error("Failed to parse exchanges", e);
        }
      } else if (filter.exchanges != null) {
        parsedExchanges = filter.exchanges;
      }

      return {
        ...filter,
        assetClassFilters: parsedAssetClassFilters,
        blockTypes: parsedBlockTypes,
        exchanges: parsedExchanges,
      };
    });
  };

  useEffect(() => {
    const allFilters = PubSub.subscribe(
      pubsubEvents.socketKeys.GET_BLOCK_FILTER_SUCCESS,
      (msg: any, data: any) => {
        if (data?.payload && Array.isArray(data.payload)) {
          const formattedFilters = processFetchedFilters(data.payload);
          setSavedFilters(formattedFilters);

          const activeFilter = formattedFilters.find(
            (f: BlockFeedFilter) => f.isActive,
          );
          if (activeFilter) {
            applySavedFilterToState(activeFilter);
          }
        }
      },
    );

    const fetchFilters = () => {
      const request = {
        action: GET_BLOCK_FEED_FILTERS_ACTION,
        requestID: uuidv4(),
        payload: {
          profileId: currentSelectedProfile,
        },
        type: "createBlockFeedFilters",
      };
      send(config.socket.channels.CORE, request);
    };

    fetchFilters();

    return () => {
      PubSub.unsubscribe(allFilters);
    };
  }, []);

  useEffect(() => {
    const addFilter = PubSub.subscribe(
      pubsubEvents.socketKeys.CREATE_BLOCK_FILTER_SUCCESS,
      (message, data) => {
        if (data?.payload.id !== null) {
          const formattedFilter = processFetchedFilters([data.payload]);
          setSavedFilters([formattedFilter[0], ...savedFilters]);
          setActiveSavedFilterId(formattedFilter[0].id);
          applySavedFilterToState(formattedFilter[0]);
        }
      },
    );

    return () => {
      PubSub.unsubscribe(addFilter);
    };
  }, [savedFilters]);

  const updateActiveFilter = (filterId: number, isActive?: boolean) => {
    const filter = savedFilters.find((f) => f.id === filterId);
    if (!filter) return;

    const updatedFilter: BlockFeedFilter = {
      id: filterId,
      profileId: currentSelectedProfile,
      filterName: filter.filterName,
      isActive: isActive ?? true,
      assetClassFilters: filter.assetClassFilters,
      blockTypes: Array.from(filter.blockTypes),
      exchanges: Array.from(filter.exchanges),
    };

    const request = {
      action: UPDATE_BLOCK_FEED_FILTER_ACTION,
      requestID: uuidv4(),
      payload: updatedFilter,
      type: "createBlockFeedFilters",
    };

    send(config.socket.channels.CORE, request);
  };

  const handleSavedFilterClick = (filterId: number) => {
    if (activeSavedFilterId === filterId) {
      clearAllFilters();
      updateActiveFilter(filterId, false);
    } else {
      updateActiveFilter(filterId);
      const filter = savedFilters.find((f) => f.id === filterId);
      applySavedFilterToState(filter!);
    }
  };

  const clearAllFilters = () => {
    setActiveSavedFilterId(null);
    setAssetClassActiveFilters({});
    SetBlockContentActiveFilter(new Set());
    setExchangesFilter(new Set());
  };

  let filterCount = 0;

  if (exchangesFilter.size > 0) filterCount++;
  if (blockContentActiveFilter.size > 0) filterCount++;
  if (Object.keys(assetClassActiveFilters).length > 0) filterCount++;

  const getFormattedDate = () => {
    if (selectedDate === "topday") return "Today";

    const datesString = selectedDate.split("-");
    const dateObj = new Date(
      Number.parseInt(datesString[2]),
      Number.parseInt(datesString[0]) - 1,
      Number.parseInt(datesString[1]),
    );
    const shortDate = dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return shortDate;
  };

  const closeSearchFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleDeleteSavedFilter = (filterId: number | undefined) => {
    if (!filterId) return;

    const request = {
      action: DELETE_BLOCK_FEED_FILTER_ACTION,
      requestID: uuidv4(),
      payload: {
        id: filterId,
      },
      type: "createBlockFeedFilters",
    };
    send(config.socket.channels.CORE, request);

    // Optimistically remove from UI
    setSavedFilters((prev) => prev.filter((f) => f.id !== filterId));

    // If the deleted filter was the active one, clear the current filters
    if (activeSavedFilterId === filterId) {
      clearAllFilters();
    }
  };

  useEffect(() => {
    // Timeout ensures the DOM updates with any newly added filters before querying
    const timer = setTimeout(() => {
      if (activeSavedFilterId && filterChipsWrapperRef.current) {
        const activeElement = filterChipsWrapperRef.current.querySelector(
          `[data-filter-id="${activeSavedFilterId}"]`,
        );
        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [activeSavedFilterId, savedFilters]);

  return (
    <>
      <div className="bf__filters-container">
        <div
          className="bf__filter-button"
          onClick={() => setIsFilterModalOpen(true)}
          style={{ flexShrink: 0 }}
        >
          <p className="ion-no-margin">
            Filters{filterCount > 0 ? ` (${filterCount})` : ""}
            {selectedDate ? ` • ${getFormattedDate()}` : ""}
          </p>
          <IonIcon
            icon={filterOutline}
            style={{
              height: "20px",
              width: "20px",
            }}
          />
        </div>
        {savedFilters.length > 0 && (
          <div className="ob__filterchips_wrapper" ref={filterChipsWrapperRef}>
            {savedFilters
              .sort((a, b) => {
                if (a.id == null && b.id == null) return 0;
                if (a.id == null) return -1;
                if (b.id == null) return 1;
                return b.id - a.id;
              })
              .map((filter) => (
                <div key={filter.id} data-filter-id={filter.id}>
                  <IonChip
                    outline={activeSavedFilterId !== filter.id}
                    onClick={() => handleSavedFilterClick(filter.id!)}
                  >
                    <IonLabel>{filter.filterName}</IonLabel>
                    <IonIcon
                      icon={closeCircle}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSavedFilter(filter.id);
                      }}
                    />
                  </IonChip>
                </div>
              ))}
          </div>
        )}
      </div>
      <DetailedBlockFeedNew
        assetClassActiveFilters={assetClassActiveFilters}
        blockContentActiveFilter={blockContentActiveFilter}
        exchangesFilter={exchangesFilter}
        selectedDate={selectedDate}
      />
      {isFilterModalOpen ? (
        <FilterModalBlockTrades
          onclose={closeSearchFilterModal}
          assetClassActiveFilters={assetClassActiveFilters}
          setAssetClassActiveFilters={setAssetClassActiveFilters}
          blockContentActiveFilter={blockContentActiveFilter}
          SetBlockContentActiveFilter={SetBlockContentActiveFilter}
          exchangesFilter={exchangesFilter}
          setExchangesFilter={setExchangesFilter}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          activeSavedFilterId={activeSavedFilterId}
          setActiveSavedFilterId={setActiveSavedFilterId}
          savedFilters={savedFilters}
          setSavedFilters={setSavedFilters}
          currentSelectedProfile={currentSelectedProfile}
          updateActiveFilter={updateActiveFilter}
        />
      ) : null}
    </>
  );
};

export default BlockFeedContentNew;
